import type { AxiosInstance, AxiosResponse } from 'axios';
import type { Question, QuestionChoice } from '@/models/question';
import type { QuestionTypeMapping } from '@/models/questionType';
import type { RootResponse } from '@/models/rootResponse';
import { buildQuestionChoicesRelation } from '@/utils/questions';

export type UpdateQuestionData = Omit<Partial<Question>, 'linked_choices'> & {
   linked_choices?: Partial<QuestionChoice>[];
};

export type UpdateQuestionType = {
   questionId: string;
   questionTypeId: string;
   questionTypesMapping: QuestionTypeMapping;
   previousChoices?: string[];
};

export type UpdateQuestionImage = {
   questionId: string;
   file: File;
};

export type UpdateQuestions = {
   questions: {
      questionId: string;
      data: UpdateQuestionData;
   }[];
};

export type CreateQuestionChoice = {
   questionId: string;
   data: Partial<QuestionChoice>;
};

export type UpdateQuestionChoice = {
   questionId: string;
   questionChoiceId: string;
   data: Partial<QuestionChoice>;
};

export type UpdateChoiceComment = {
   questionChoiceId: string;
   commentId: string | null;
   message: string;
};

export type MoveQuestionParentType = 'page' | 'question_choice';

export type MoveQuestion = {
   questionId: string;
   newParentType: MoveQuestionParentType;
   newParentId: string;
   order: number;
};

class QuestionService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async updateQuestionType({
      questionId,
      questionTypeId,
      questionTypesMapping,
      previousChoices,
   }: UpdateQuestionType): Promise<void> {
      const questionChoicesRelation = buildQuestionChoicesRelation({
         questionTypeId,
         questionTypesMapping,
         previousChoices,
      });

      await this.provider.post('rest/questions/mutate', {
         mutate: [
            {
               operation: 'update',
               key: questionId,
               attributes: {
                  question_type_id: questionTypeId,
               },
               ...(questionChoicesRelation ?? {}),
            },
         ],
      });
   }

   async updateQuestionImage({
      questionId,
      file,
   }: UpdateQuestionImage): Promise<void> {
      const formData = new FormData();

      formData.append('mutate[0][operation]', 'update');
      formData.append('mutate[0][key]', questionId);
      formData.append('mutate[0][media][0][file]', file);
      formData.append('mutate[0][media][0][collection_name]', 'question');

      await this.provider.post('rest/questions/mutate', formData, {
         headers: {
            'Content-Type': 'multipart/form-data',
         },
      });
   }

   async updateQuestions({ questions }: UpdateQuestions): Promise<void> {
      await this.provider.post('rest/questions/mutate', {
         mutate: questions.map(({ questionId, data }) => {
            const { linked_choices, ...attributes } = data;

            const questionChoices =
               linked_choices
                  ?.filter(
                     (choice): choice is QuestionChoice & { id: string } =>
                        choice !== undefined &&
                        choice !== null &&
                        'id' in choice &&
                        typeof choice.id === 'string',
                  )
                  .map(({ id, label, answers_limit }) => ({
                     operation: 'update',
                     key: String(id),
                     attributes: {
                        label,
                        answers_limit,
                     },
                  })) ?? [];

            const relationObj =
               questionChoices.length > 0 ? { questionChoices } : undefined;

            return {
               operation: 'update',
               key: questionId,
               attributes: attributes,
               ...(relationObj ? { relations: relationObj } : {}),
            };
         }),
      });
   }

   async createQuestionChoice({
      questionId,
      data,
   }: CreateQuestionChoice): Promise<void> {
      await this.provider.post('rest/questions/mutate', {
         mutate: [
            {
               operation: 'update',
               key: questionId,
               relations: {
                  questionChoices: [
                     {
                        operation: 'create',
                        attributes: data,
                     },
                  ],
               },
            },
         ],
      });
   }

   async updateQuestionChoice({
      questionId,
      questionChoiceId,
      data,
   }: UpdateQuestionChoice): Promise<void> {
      await this.provider.post('rest/questions/mutate', {
         mutate: [
            {
               operation: 'update',
               key: questionId,
               relations: {
                  questionChoices: [
                     {
                        operation: 'update',
                        key: questionChoiceId,
                        attributes: data,
                     },
                  ],
               },
            },
         ],
      });
   }

   async updateChoiceComment({
      questionChoiceId,
      commentId,
      message,
   }: UpdateChoiceComment): Promise<void> {
      await this.provider.post('rest/question-choices/mutate', {
         mutate: [
            {
               operation: 'update',
               key: questionChoiceId,
               relations: {
                  comment: commentId
                     ? {
                          operation: 'update',
                          key: commentId,
                          attributes: { message },
                       }
                     : {
                          operation: 'create',
                          attributes: { message },
                       },
               },
            },
         ],
      });
   }

   async duplicateQuestion(questionId: string): Promise<void> {
      await this.provider.post('rest/questions/actions/duplicate', {
         fields: [
            {
               name: 'question_id',
               value: questionId,
            },
         ],
      });
   }

   async moveQuestion({
      questionId,
      newParentType,
      newParentId,
      order,
   }: MoveQuestion): Promise<void> {
      await this.provider.post('rest/questions/actions/move', {
         fields: [
            { name: 'question_id', value: questionId },
            { name: 'new_parent_type', value: newParentType },
            { name: 'new_parent_id', value: newParentId },
            { name: 'order', value: order },
         ],
      });
   }

   async deleteChoiceComment(commentId: string): Promise<void> {
      await this.provider.delete('rest/comments', {
         data: {
            resources: [commentId],
         },
      });
   }

   async deleteQuestion(questionId: string): Promise<void> {
      await this.provider.delete('rest/questions', {
         data: {
            resources: [questionId],
         },
      });
   }

   async searchQuestionsWithChoices(surveyId: string): Promise<Question[]> {
      const response: AxiosResponse<RootResponse<Question[]>> =
         await this.provider.post('rest/questions/search', {
            search: {
               filters: [{ field: 'page.survey_id', value: surveyId }],
               fields: ['id', 'label', 'order', 'question_type'],
               includes: [
                  {
                     relation: 'linkedChoices',
                     sorts: [{ field: 'order', direction: 'asc' }],
                  },
                  { relation: 'questionType' },
               ],
               sorts: [{ field: 'order', direction: 'asc' }],
               limit: 50,
            },
         });

      return response.data.data.filter(
         (question) =>
            question.linked_choices && question.linked_choices.length > 0,
      );
   }
}

export default QuestionService;
